import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConfigurationService} from '../../../shared/configuration.service';
import {first} from 'rxjs/operators';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {Subject} from 'rxjs';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {AbstractControl, FormControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {isDefined} from '../../../commons/commons';
import {TimeFilter, TimeFilterProfile, TimeFilterType} from '../../../shared/time-filter/time-filter.component';
import {isCurrentDay, isCurrentMonth, loadBillingPeriod} from '../../../shared/ec-economics';
import {ActivatedRoute} from '@angular/router';

export enum Mode {
    SINGLE = 'Single',
    RANGE = 'Range'
}

export interface BillingPeriodConfig {
    pastDate?: number;
    futureDate?: number;
}

export interface BillingPeriod {
    fromTime: number;
    toTime: number;
    mode: Mode;
    type?: TimeFilterType;
}

const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MMMM YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};

const ALLOWED_TYPES_VALUES = {
    [TimeFilterType.THIS_YEAR]: 31536000000,
    [TimeFilterType.LAST_YEAR]: 31536000000,
    [TimeFilterType.SINGLE_MONTH]: 2628000000,
    [TimeFilterType.LAST_MONTH]: 2628000000,
    [TimeFilterType.LAST_3_MONTHS]: 7884000000,
    [TimeFilterType.LAST_6_MONTHS]: 15768000000
};
const ALLOWED_TYPES_WHITELIST = [
    TimeFilterType.THIS_MONTH,
    TimeFilterType.RANGE
];

@Component({
    selector: 'app-ec-billing-filter',
    templateUrl: './ec-billing-filter.component.html',
    styleUrls: ['./ec-billing-filter.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS}
    ]
})
export class EcBillingFilterComponent implements OnInit, OnChanges {

    private timeFilter: TimeFilter = null;
    private currentMonth = false;
    private currentFilterType: TimeFilterType = null;
    private firstLoadAfterPageChangeSingleMode = true;
    private firstLoadAfterPageChangeCustomRangeMode = true;

    @Input() config: BillingPeriodConfig;
    @Input() billingPeriod: BillingPeriod;
    @Input() useFilter: boolean;
    @Output() billingPeriodChange: EventEmitter<BillingPeriod> = new EventEmitter<BillingPeriod>();

    timeZone: string = null;
    timeMode: Mode = null;
    singleDate: FormControl = new FormControl();
    startDate: FormControl;
    endDate: FormControl;
    minEndDate: Moment;
    maxStartDate: Moment;
    minStartDate: Moment;
    futureDate: Moment;
    maxEndDate: Moment;
    allowedTypes: TimeFilterType[] = [];
    disabledTypes: TimeFilterType[] = [];
    setType: Subject<TimeFilterType> = new Subject<TimeFilterType>();

    readonly TimeFilterProfile = TimeFilterProfile;
    readonly Mode = Mode;

    constructor(private readonly configurationService: ConfigurationService,
                private readonly route: ActivatedRoute) {
        this.startDate = new FormControl(null, [this.crossYearValidation('start')]);
        this.endDate = new FormControl(null, [this.crossYearValidation('end')]);
    }

    ngOnInit() {
        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('config' in changes && !changes.config.isFirstChange()) {
            this.updateCfg(changes.config.currentValue);
            this.getAllowedTypes();
        }
        if ('billingPeriod' in changes && !changes.billingPeriod.isFirstChange()) {
            this.loadBillingPeriod(changes.billingPeriod.currentValue);
        }
    }

    // Detect set date from datepicker
    onSetDate(monthAndYear: Moment, type: 'single' | 'start' | 'end') {
        const date = moment.tz({
            year: monthAndYear.year(),
            month: monthAndYear.month(),
            day: 1,
            hour: 0
        }, this.timeZone);
        // Manage single month
        if (type === 'single') {
            this.singleDate.setValue(date);
            this.currentMonth = isCurrentMonth(date, this.timeZone);
        } else if (type === 'start') { // Manage start input date
            this.startDate.setValue(date.clone().startOf('month'));
            this.minEndDate = date.clone().startOf('month');
        } else if (type === 'end') { // Manage end input date
            this.endDate.setValue(date.clone().startOf('month'));
            this.maxStartDate = date.clone().endOf('month');
        }
        // If user change data from date-pickers, set mat-option to RANGE
        if (this.timeMode !== Mode.SINGLE) {
            // Emit range filter type to change mat-option
            this.currentFilterType = TimeFilterType.RANGE;
            this.setType.next(TimeFilterType.RANGE);
        }
        // Emit billing period if formControls are valid
        if (type === 'single') {
            this.singleDate.markAsTouched();
            if (this.singleDate.valid) {
                this.notifyBillingPeriodChange();
            }
        } else {
            this.startDate.markAsTouched();
            this.endDate.markAsTouched();
            this.startDate.updateValueAndValidity();
            this.endDate.updateValueAndValidity();
            if (this.startDate.valid && this.endDate.valid) {
                this.notifyBillingPeriodChange(null, type);
            }
        }
    }

    // Manage when user change time filter in mat-option components
    onSelectTimeFilter(event: TimeFilter) {
        this.timeFilter = event;
        this.currentFilterType = event?.type;
        // Manage ec with activation date in future or with no activation date
        if (!isDefined(this.config?.pastDate) && isDefined(this.config?.futureDate) ||
            !isDefined(this.config?.pastDate) && !isDefined(this.config?.futureDate)) {
            this.currentFilterType = TimeFilterType.THIS_MONTH;
            this.setType.next(TimeFilterType.THIS_MONTH);
            if (this.timeFilter) {
                this.timeFilter.range = () => {
                    const now = moment.tz(this.timeZone).startOf('hour');
                    return {
                        fromTime: now.clone().startOf('month').valueOf(),
                        toTime: now.valueOf(),
                        includesNow: true
                    };
                };
            }
        }
        // Set single mode with single month selection
        if (this.timeFilter) {
            if (this.timeFilter.type === TimeFilterType.SINGLE_MONTH) {
                this.timeMode = Mode.SINGLE;
            } else {
                // With THIS_YEAR selection, hide possible errors in end date
                if (this.timeFilter?.type === TimeFilterType.THIS_YEAR) {
                    setTimeout(() => {
                        this.endDate.setErrors(null);
                    }, 0);
                }
                this.timeMode = Mode.RANGE;
            }
        }
        this.notifyBillingPeriodChange(true);
    }

    // Check if start and end date have cross-year error
    crossYearValidation(currDate: string): ValidatorFn | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const date = currDate === 'start' ? this.endDate : this.startDate;
            if (date) {
                const dateYear = (date.value as Moment)?.year();
                const currentYear = (control.value as Moment)?.year();
                const isNotValid = isDefined(dateYear) && isDefined(currentYear) && dateYear !== currentYear;
                return isNotValid ? {crossYear: true} : null;
            }
            return null;
        };
    }

    // Set min/max date for start and end
    private updateCfg(cfg: BillingPeriodConfig) {
        this.minStartDate = isDefined(cfg?.pastDate) ?
            moment.tz(cfg.pastDate, this.timeZone).startOf('month') :
            moment.tz(this.timeZone).startOf('month');
        this.futureDate = isDefined(cfg?.futureDate) ?
            moment.tz(cfg.futureDate, this.timeZone).startOf('month') :
            moment.tz(this.timeZone).startOf('month');
        this.minEndDate = this.minStartDate;
        this.maxStartDate = this.futureDate;
        this.maxEndDate = isDefined(cfg?.futureDate) ?
            moment.tz(cfg.futureDate, this.timeZone).startOf('month') :
            moment.tz(this.timeZone);
        if ((this.singleDate.value && cfg?.pastDate > (this.singleDate.value as Moment).valueOf()) ||
            (this.startDate.value && cfg?.pastDate > (this.startDate.value as Moment).valueOf())) {
            this.setDefaultBillingPeriod();
        }
    }

    private loadBillingPeriod(period: BillingPeriod) {
        // If period is null -> first time user navigate to ec aggregated, so set default billing period
        if (!period) {
            this.setDefaultBillingPeriod();
        } else {
            // Set date based on query params
            switch (period.mode) {
                case Mode.SINGLE: {
                    const periodDate = moment.tz(period.fromTime, this.timeZone);
                    const date = moment.tz({
                        year: periodDate.year(),
                        month: periodDate.month(),
                        day: 1,
                        hour: 0
                    }, this.timeZone);
                    this.timeMode = Mode.SINGLE;
                    this.singleDate.setValue(date);
                    this.currentMonth = isCurrentMonth(date, this.timeZone);
                    this.minEndDate = this.minStartDate;
                    this.maxStartDate = this.futureDate;
                    break;
                }
                case Mode.RANGE: {
                    const periodStartDate = moment.tz(period.fromTime, this.timeZone);
                    const periodEndDate = moment.tz(period.toTime, this.timeZone);
                    const startDate = moment.tz({
                        year: periodStartDate.year(),
                        month: periodStartDate.month(),
                        day: 1,
                        hour: 0
                    }, this.timeZone);
                    const endDate = moment.tz({
                        year: periodEndDate.year(),
                        month: periodEndDate.month(),
                        date: periodEndDate.date(), // .day() -> day of week, .date() -> day of month
                        hour: 0
                    }, this.timeZone);
                    if (!isCurrentDay(endDate, this.timeZone)) {
                        endDate.subtract(1, 'month');
                    }
                    const nowMoment = moment.tz(this.timeZone);
                    this.currentMonth = isCurrentMonth(endDate, this.timeZone);
                    if (this.currentMonth) {
                        this.endDate.setValue(nowMoment.startOf('hour'));
                    } else {
                        this.endDate.setValue(endDate.clone().endOf('month'));
                    }
                    this.timeMode = Mode.RANGE;
                    this.startDate.setValue(startDate.clone().startOf('month'));
                    this.singleDate.setValue(endDate.clone().startOf('hour'));
                    this.minEndDate = startDate.clone().startOf('month');
                    const end = endDate.clone().endOf('month');
                    if (end.valueOf() > nowMoment.valueOf()) {
                        this.maxStartDate = nowMoment.startOf('hour');
                    } else {
                        this.maxStartDate = endDate.clone().endOf('month');
                    }
                    this.startDate.updateValueAndValidity();
                    this.endDate.updateValueAndValidity();
                    break;
                }
            }
        }
    }

    // Emit billing period to parent component with ranges
    private notifyBillingPeriodChange(useTimeFilter?: boolean, type?: string) {

        let missingCustomRangeUserInput = false;
        if (this.timeFilter && this.timeFilter.type === TimeFilterType.RANGE && useTimeFilter && !this.firstLoadAfterPageChangeCustomRangeMode) {
            this.startDate.setValue(null);
            this.endDate.setValue(null);
            missingCustomRangeUserInput = true;
        }

        if (!this.billingPeriod || Object.keys(this.billingPeriod).length === 0) {
            this.billingPeriod = loadBillingPeriod(this.route.snapshot.queryParamMap);
        }
        const mode: Mode = this.timeMode;
        switch (mode) {
            case Mode.SINGLE: {
                let date: Moment = null;
                let currentMonth: boolean = null;

                if (!this.singleDate.value) {
                    const periodDate = moment.tz(this.billingPeriod.fromTime, this.timeZone);
                    date = moment.tz({
                        year: periodDate.year(),
                        month: periodDate.month(),
                        day: 1,
                        hour: 0
                    }, this.timeZone);
                    this.singleDate.setValue(date);
                    this.currentMonth = isCurrentMonth(date, this.timeZone);
                    this.minEndDate = this.minStartDate;
                    this.maxStartDate = this.futureDate;
                } else {
                    date = this.singleDate.value;
                    currentMonth = isCurrentMonth(date, this.timeZone);
                }

                if (date && (!currentMonth || !!this.timeFilter)) {
                    const {fromTime, toTime} = useTimeFilter ?
                        this.timeFilter.range() :
                        {fromTime: date.valueOf(), toTime: date.clone().add(1, 'month').valueOf()};

                    let periodUpdate = {fromTime, toTime, mode, type: this.timeFilter.type} as BillingPeriod;

                    if (this.firstLoadAfterPageChangeSingleMode) {
                        periodUpdate = this.billingPeriod;
                    }

                    this.billingPeriodChange.next(periodUpdate);
                }
                this.firstLoadAfterPageChangeSingleMode = false;
                break;
            }
            case Mode.RANGE: {
                const startDate: Moment = this.startDate.value;
                const endDate: Moment = this.isBillingPeriodToTimeWinning(type) ?
                    moment.tz(this.billingPeriod.toTime, this.timeZone) :
                    this.endDate.value;
                if (useTimeFilter && !!this.timeFilter && !missingCustomRangeUserInput) {
                    const range = this.timeFilter.range() || {
                        fromTime: this.billingPeriod.fromTime,
                        toTime: this.billingPeriod.toTime, includesNow: false
                    };
                    if (range) {
                        let fromTime;
                        if (this.timeFilter.type === TimeFilterType.THIS_YEAR || this.timeFilter.type === TimeFilterType.LAST_YEAR) {
                            // if activation date is after fromTime, we either pick it or current date
                            if (range.fromTime < this.minStartDate?.valueOf()) {
                                fromTime = Math.min(this.minStartDate?.valueOf(), moment.tz(this.timeZone).startOf('day').valueOf());
                            } else {
                                fromTime = range.fromTime;
                            }
                        } else {
                            fromTime = range.fromTime;
                        }
                        this.billingPeriodChange.next({
                            fromTime,
                            toTime: range.toTime,
                            mode,
                            type: this.timeFilter.type
                        });
                        this.firstLoadAfterPageChangeCustomRangeMode = false;
                    }
                } else if (startDate && endDate) {
                    let toTime = endDate.clone();
                    if (isCurrentMonth(toTime, this.timeZone)) {
                        toTime = moment.tz(this.timeZone).startOf('hour');
                    } else if (!this.isBillingPeriodToTimeWinning(type)) {
                        toTime = toTime.add(1, 'month');
                    }
                    this.billingPeriodChange.next({
                        fromTime: startDate.valueOf(),
                        toTime: toTime.valueOf(),
                        mode,
                        type: TimeFilterType.RANGE
                    });
                    missingCustomRangeUserInput = false;
                }
                this.firstLoadAfterPageChangeSingleMode = false;
                break;
            }
        }
    }

    private isBillingPeriodToTimeWinning(type: string) {
        return type && type === 'start' && this.billingPeriod && this.endDate.value &&
            this.startDate.value.year() === moment.tz(this.billingPeriod.fromTime, this.timeZone).year();
    }

    // Set default billing period to whole year
    private setDefaultBillingPeriod() {
        const now = moment.tz(this.timeZone);
        const date = moment.tz({
            year: now.year(),
            month: 0,
            day: 1,
            hour: 0
        }, this.timeZone);
        this.timeMode = Mode.RANGE;
        if (this.minStartDate?.valueOf() >= date.valueOf()) {
            this.startDate.setValue(this.minStartDate);
        } else {
            this.startDate.setValue(date);
        }
        this.singleDate.setValue(now.startOf('hour'));
        this.endDate.setValue(now.startOf('hour'));
        this.currentMonth = false;
        this.minEndDate = this.minStartDate;
        this.maxStartDate = this.futureDate;
    }

    private getAllowedTypes() {
        const now = moment.tz(this.timeZone);
        const currentYear = now.year();
        // Filter types to show in filter types option
        this.allowedTypes = Object.entries(ALLOWED_TYPES_VALUES)
            .map(allowedType => allowedType[0] as TimeFilterType);
        this.allowedTypes.push(...ALLOWED_TYPES_WHITELIST);
        // These are types that need to be disabled in time filter
        this.disabledTypes = Object.entries(ALLOWED_TYPES_VALUES)
            .map(([type, duration]) => {
                const timeAgo = now.clone().subtract(duration, 'milliseconds');
                const yearTimeAgo = timeAgo.year();
                // Disable last 3/6 months if cross-year
                if ((type === TimeFilterType.LAST_3_MONTHS || type === TimeFilterType.LAST_6_MONTHS) && yearTimeAgo !== currentYear) {
                    return type as TimeFilterType;
                }
                // Disable last month if activation cer date is recent than month ago
                if (type === TimeFilterType.LAST_MONTH && timeAgo.valueOf() < this.minStartDate?.valueOf()) {
                    return type as TimeFilterType;
                }
                // Disable last year if activation cer date is recent than year ago and have different year
                if (type === TimeFilterType.LAST_YEAR && timeAgo.valueOf() < this.minStartDate?.valueOf() && yearTimeAgo !== this.minStartDate?.year()) {
                    return type as TimeFilterType;
                }
                return null;
            })
            .filter(Boolean) as TimeFilterType[];
    }

    // Remove errors if in THIS_YEAR period, user clicks on end date end click out of datepicker
    closePicker() {
        if (this.currentFilterType === TimeFilterType.THIS_YEAR) {
            setTimeout(() => {
                this.endDate.setErrors(null);
            }, 0);
        }
    }
}

